import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { formatPrice } from "@/lib/format";

export default function Projects() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("/projects")
      .then((r) => setItems(r.data.items))
      .catch(() => setError("تعذر تحميل المشاريع، حاول مرة أخرى"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <Seo title="المشاريع" description="مشاريع سكنية افتراضية في جدة والرياض والدمام — نموذج تجريبي لموقع وساطة عقارية." />
      <section className="bg-forest py-16 md:py-24">
        <div className="container-lux space-y-4">
          <p className="text-gold text-sm font-heading font-semibold tracking-widest">مشاريعنا</p>
          <h1 className="text-3xl md:text-5xl font-heading font-bold text-ivory tracking-tight">مشاريع سكنية نموذجية</h1>
          <p className="text-ivory/70 max-w-xl">مجمعات سكنية افتراضية لعرض أسلوب تقديم المشاريع — جميع البيانات تجريبية.</p>
        </div>
      </section>

      <section className="container-lux py-14 md:py-20" data-testid="projects-list">
        {error && <div className="rounded-xl bg-destructive/10 text-destructive px-5 py-4 text-sm" data-testid="projects-error">{error}</div>}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[1, 2].map((n) => <div key={n} className="h-96 animate-pulse rounded-3xl bg-forest/5" />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {items.map((pr, i) => (
              <Reveal key={pr.id} delay={i * 0.08}>
                <Link to={`/projects/${pr.id}`} className="card-lux group block overflow-hidden rounded-3xl" data-testid={`project-list-card-${pr.id}`}>
                  <div className="relative h-72 overflow-hidden">
                    <img src={pr.images?.[0]} alt={pr.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <span className="absolute top-4 start-4 rounded-full bg-gold px-4 py-1.5 text-xs font-heading font-bold text-forest">{pr.status}</span>
                  </div>
                  <div className="p-7 space-y-4">
                    <div className="space-y-1.5">
                      <h2 className="font-heading font-bold text-2xl text-forest">{pr.name}</h2>
                      <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                        <MapPin className="h-4 w-4 text-gold-dark" aria-hidden="true" />
                        {pr.location || pr.city}
                      </p>
                    </div>
                    <p className="text-sm text-charcoal/75 leading-relaxed line-clamp-2">{pr.description}</p>
                    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-forest/10 pt-4">
                      <div className="flex gap-2">
                        {pr.unit_types?.map((t) => (
                          <span key={t} className="rounded-full bg-forest/5 px-3 py-1 text-xs font-heading font-semibold text-forest">{t}</span>
                        ))}
                      </div>
                      {pr.starting_price && (
                        <span className="text-sm font-heading font-bold text-gold-dark">تبدأ من {formatPrice(pr.starting_price)}</span>
                      )}
                    </div>
                    <span className="inline-flex items-center gap-2 text-sm font-heading font-semibold text-forest group-hover:text-gold-dark transition-colors">
                      تفاصيل المشروع <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </section>

      <section className="container-lux pb-20">
        <div className="rounded-3xl border border-gold/25 bg-white p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-2">
            <Eyebrow>مشروعك القادم</Eyebrow>
            <h2 className="font-heading font-bold text-2xl text-forest">تبحث عن وحدة داخل مشروع معيّن؟</h2>
            <p className="text-sm text-muted-foreground">أرسل مواصفاتك وسنرشّح لك الوحدات المناسبة.</p>
          </div>
          <Link to="/request" className="btn-gold shrink-0" data-testid="projects-cta-request">اطلب عقارك</Link>
        </div>
      </section>
    </>
  );
}
